import React, { useContext } from 'react';
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from 'use-places-autocomplete';
import classNames from 'classnames';
import { Combobox, Popover } from '@headlessui/react';
import { UserContext } from './locationFinder';
import LocationFilters from './LocationFilters';

function Places() {
  const {
    value,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: {
        country: ['ca'],
      },
    },
  });

  const { handleUpdateUserPosition } = useContext(UserContext);

  const handleSelect = async (val) => {
    setValue(val, false);
    clearSuggestions();
    const results = await getGeocode({ address: val });
    const newPosition = await getLatLng(results[0]);
    handleUpdateUserPosition(newPosition);
  };

  return (
    <Combobox
      as="div"
      value={value}
      onChange={handleSelect}
      className="tw-flex tw-h-full ">
      <Combobox.Label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-sr-only">
        Assigned to
      </Combobox.Label>
      <div className="tw-relative tw-flex-1">
        <div className="tw-absolute tw-z-10 tw-left-3 tw-top-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="tw-w-6 tw-h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <Combobox.Input
          className="tw-border-none tw-w-full tw-h-full tw-py-2 tw-pr-10 tw-bg-white tw-border-collapse tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-pl-11 focus:tw-border-blue focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue sm:tw-text-sm"
          onChange={(event) => setValue(event.target.value)}
        />

        {status === 'OK' && (
          <Combobox.Options className="tw-absolute tw-p-0 tw-z-10 tw-w-full tw-py-1 tw-mt-1 tw-overflow-auto tw-text-base tw-bg-white tw-rounded-md tw-shadow-lg tw-max-h-60 tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none sm:tw-text-sm">
            {data.map(({ place_id, description }) => (
              <Combobox.Option
                key={place_id}
                value={description}
                className={({ active }) =>
                  classNames(
                    'tw-relative tw-cursor-default tw-select-none tw-py-2 tw-pl-3 tw-pr-9 tw-list-none',
                    active ? 'tw-bg-blue tw-text-white' : 'tw-text-gray-900'
                  )
                }>
                {({ active, selected }) => (
                  <>
                    <span
                      className={classNames(
                        'tw-block tw-truncate',
                        selected && 'tw-font-semibold'
                      )}>
                      {description}
                    </span>

                    {selected && (
                      <span
                        className={classNames(
                          'tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-4',
                          active ? 'tw-text-white' : 'tw-text-blue'
                        )}></span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
}

const LocationBar = () => {
  return (
    <div className="tw-relative tw-bg-blue">
      <div className="tw-flex tw-gap-4 tw-p-4 tw-h-20">
        <div className="tw-flex-1 ">
          <Places />
        </div>
        {/* <Popover>
          <Popover.Button className="tw-p-2 tw-text-white tw-rounded-md tw-bg-blue-light tw-border-none">
            <svg
              className="tw-w-8 tw-h-8"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.875 21.875C12.254 21.875 11.75 21.371 11.75 20.75C11.75 20.129 12.254 19.625 12.875 19.625C13.496 19.625 14 20.129 14 20.75C14 21.371 13.496 21.875 12.875 21.875ZM24.125 19.625H16.0419C15.5761 18.3189 14.3398 17.375 12.875 17.375C11.4102 17.375 10.1739 18.3189 9.70813 19.625H3.875C3.25288 19.625 2.75 20.1279 2.75 20.75C2.75 21.3721 3.25288 21.875 3.875 21.875H9.70813C10.1739 23.1811 11.4102 24.125 12.875 24.125C14.3398 24.125 15.5761 23.1811 16.0419 21.875H24.125C24.7471 21.875 25.25 21.3721 25.25 20.75C25.25 20.1279 24.7471 19.625 24.125 19.625ZM21.875 15.125C21.254 15.125 20.75 14.621 20.75 14C20.75 13.379 21.254 12.875 21.875 12.875C22.496 12.875 23 13.379 23 14C23 14.621 22.496 15.125 21.875 15.125ZM21.875 10.625C20.4102 10.625 19.1739 11.5689 18.7081 12.875H3.875C3.25288 12.875 2.75 13.3779 2.75 14C2.75 14.6221 3.25288 15.125 3.875 15.125H18.7081C19.1739 16.4311 20.4102 17.375 21.875 17.375C23.7358 17.375 25.25 15.8608 25.25 14C25.25 12.1392 23.7358 10.625 21.875 10.625ZM8.375 6.125C8.996 6.125 9.5 6.629 9.5 7.25C9.5 7.871 8.996 8.375 8.375 8.375C7.754 8.375 7.25 7.871 7.25 7.25C7.25 6.629 7.754 6.125 8.375 6.125ZM3.875 8.375H5.20812C5.67387 9.68113 6.91025 10.625 8.375 10.625C9.83975 10.625 11.0761 9.68113 11.5419 8.375H24.125C24.7471 8.375 25.25 7.87212 25.25 7.25C25.25 6.62788 24.7471 6.125 24.125 6.125H11.5419C11.0761 4.81887 9.83975 3.875 8.375 3.875C6.91025 3.875 5.67387 4.81887 5.20812 6.125H3.875C3.25288 6.125 2.75 6.62788 2.75 7.25C2.75 7.87212 3.25288 8.375 3.875 8.375Z"
                fill="currentColor"
              />
            </svg>
          </Popover.Button>
          <Popover.Panel className="tw-absolute tw-left-0 tw-z-10 tw-w-full tw-px-4 tw-py-4 tw-bg-yellow tw-drop-shadow-md tw-top-full ">
            <LocationFilters />
          </Popover.Panel>
        </Popover> */}
      </div>
    </div>
  );
};

export default LocationBar;
